/**
 * PdfCoordinateService — conversión centralizada entre coordenadas de pantalla
 * y coordenadas PDF (SPEC §8-9). Única fuente de verdad para la geometría del
 * PDFFormBuilder; no duplicar esta lógica en los componentes.
 *
 * Convenciones:
 *  - PANTALLA: píxeles CSS relativos a la esquina superior-izquierda de la
 *    página renderizada. El eje Y crece hacia ABAJO (convención HTML).
 *  - PDF: puntos (1 pt = 1/72") relativos a la esquina inferior-izquierda.
 *    El eje Y crece hacia ARRIBA (convención PDF nativa). Es lo que se
 *    almacena en template.json y lo que consume el motor de generación.
 *
 * Independencia de escala: la página se mide en puntos PDF (pageWidthPt /
 * pageHeightPt, obtenidos del evento `page-size` de vue-pdf-signature a
 * scale 1). La escala de render se deriva del ancho realmente pintado del
 * canvas. Así, el zoom o un contenedor de distinto ancho no alteran las
 * coordenadas PDF almacenadas (no hay drift).
 *
 *   screenX = pdfX * scale
 *   screenY = (pageHeightPt - pdfY - pdfHeight) * scale   // flip de Y
 *
 *   pdfX = screenX / scale
 *   pdfY = pageHeightPt - (screenY / scale) - pdfHeight   // flip de Y
 */

export default class PdfCoordinateService {
  /**
   * @param {Object} params
   * @param {number} params.pageWidthPt   ancho de la página en puntos PDF (scale 1)
   * @param {number} params.pageHeightPt  alto de la página en puntos PDF (scale 1)
   * @param {number} [params.renderedWidthPx] ancho actual del canvas en px CSS
   */
  constructor({ pageWidthPt, pageHeightPt, renderedWidthPx } = {}) {
    this.setPageSize(pageWidthPt, pageHeightPt);
    this.setRenderedWidth(renderedWidthPx != null ? renderedWidthPx : pageWidthPt);
  }

  /**
   * Define el tamaño de la página en puntos PDF.
   * @param {number} widthPt
   * @param {number} heightPt
   */
  setPageSize(widthPt, heightPt) {
    this.pageWidthPt = toPositive(widthPt);
    this.pageHeightPt = toPositive(heightPt);
    return this;
  }

  /**
   * Define el ancho actualmente renderizado (px CSS) del canvas de la página.
   * De aquí sale el factor de escala.
   * @param {number} renderedWidthPx
   */
  setRenderedWidth(renderedWidthPx) {
    this.renderedWidthPx = toPositive(renderedWidthPx) || this.pageWidthPt;
    return this;
  }

  /**
   * Factor de escala actual (px CSS por punto PDF).
   * @returns {number}
   */
  get scale() {
    if (!this.pageWidthPt) {
      return 1;
    }
    return this.renderedWidthPx / this.pageWidthPt;
  }

  /** Alto renderizado derivado (px CSS). @returns {number} */
  get renderedHeightPx() {
    return this.pageHeightPt * this.scale;
  }

  // --- PDF -> PANTALLA -------------------------------------------------------

  /**
   * Convierte un rect en puntos PDF (origen abajo-izq) a px de pantalla
   * (origen arriba-izq), listo para posicionar el overlay.
   * @param {{x:number,y:number,width:number,height:number}} pdfRect
   * @returns {{x:number,y:number,width:number,height:number}}
   */
  pdfRectToScreen({ x, y, width, height }) {
    const { scale, pageHeightPt } = this;
    return {
      x: x * scale,
      y: (pageHeightPt - y - height) * scale,
      width: width * scale,
      height: height * scale,
    };
  }

  // --- PANTALLA -> PDF -------------------------------------------------------

  /**
   * Convierte un rect en px de pantalla (origen arriba-izq) a puntos PDF
   * (origen abajo-izq) para almacenar en el template.
   * @param {{x:number,y:number,width:number,height:number}} screenRect
   * @returns {{x:number,y:number,width:number,height:number}}
   */
  screenRectToPdf({ x, y, width, height }) {
    const { scale, pageHeightPt } = this;
    const pdfWidth = width / scale;
    const pdfHeight = height / scale;
    return {
      x: x / scale,
      y: pageHeightPt - (y / scale) - pdfHeight,
      width: pdfWidth,
      height: pdfHeight,
    };
  }

  /**
   * Convierte un punto (px pantalla, origen arriba-izq) a punto PDF
   * (origen abajo-izq). Útil para clicks.
   * @param {{x:number,y:number}} point
   * @returns {{x:number,y:number}}
   */
  screenPointToPdf({ x, y }) {
    const { scale, pageHeightPt } = this;
    return {
      x: x / scale,
      y: pageHeightPt - (y / scale),
    };
  }

  // --- Utilidades de borde ---------------------------------------------------

  /**
   * Restringe un rect PDF para que quede completamente dentro de la página.
   * @param {{x:number,y:number,width:number,height:number}} pdfRect
   * @returns {{x:number,y:number,width:number,height:number}}
   */
  clampPdfRect({ x, y, width, height }) {
    const w = clamp(width, 0, this.pageWidthPt);
    const h = clamp(height, 0, this.pageHeightPt);
    return {
      x: clamp(x, 0, this.pageWidthPt - w),
      y: clamp(y, 0, this.pageHeightPt - h),
      width: w,
      height: h,
    };
  }
}

function toPositive(n) {
  const v = Number(n);
  return Number.isFinite(v) && v > 0 ? v : 0;
}

function clamp(n, min, max) {
  const v = Number(n) || 0;
  if (max < min) {
    return min;
  }
  return Math.min(Math.max(v, min), max);
}
